import { useQuery } from '@tanstack/react-query';
import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldContent, FieldGroup } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import { handleApiError } from '../lib/errors';
import type { EmployeeProjectModel } from '../services/employeeProjectModelService';
import { employeeService } from '../services/employeeService';
import { orderService } from '../services/orderService';
import { formatDateDdMmYyyy } from '../utils/dateUtils';
import { logger } from '../utils/logger';

const WORK_TYPE_LABELS: Record<string, string> = {
  office: 'Office',
  site: 'Site',
  both: 'Both',
};

interface EmployeeProjectModelFormData {
  employee_id: string;
  project_id: string;
  sales_rate: string;
  work_type: string;
  start_date: string;
  end_date: string;
  no_end_date: boolean;
  status?: string;
  order: string;
}

interface EmployeeProjectModelDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (model: Partial<EmployeeProjectModel>) => void;
  model?: EmployeeProjectModel | null;
  isEdit?: boolean;
  projectId?: string;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const EmployeeProjectModelDrawer: React.FC<EmployeeProjectModelDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  model,
  isEdit = false,
  projectId,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<EmployeeProjectModelFormData>({
    employee_id: '',
    project_id: '',
    sales_rate: '',
    work_type: '',
    start_date: '',
    end_date: '',
    no_end_date: false,
    order: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: employeesData, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['active-employees-simple'],
    queryFn: () => employeeService.getActiveEmployeesSimple(),
    enabled: isOpen && !readOnly,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const currentProjectId = formData.project_id || projectId || '';
  const { data: ordersData, isLoading: isLoadingOrders } = useQuery({
    queryKey: ['orders', currentProjectId],
    queryFn: () => orderService.getOrdersByProject(currentProjectId),
    enabled: isOpen && !readOnly && currentProjectId !== '',
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const employeeOptions = useMemo(
    () =>
      (employeesData ?? []).map((emp) => ({
        value: emp.employee_id,
        label: `${emp.name} (${emp.employee_id})`,
      })),
    [employeesData]
  );

  const orderOptions = useMemo(() => {
    const opts = [{ value: '', label: 'Not Applicable' }];
    if (ordersData?.length) {
      for (const order of ordersData) {
        opts.push({ value: order.order_number, label: order.order_number });
      }
    }
    return opts;
  }, [ordersData]);

  const employeeLabel = useMemo(() => {
    const employee = model?.employee;
    if (typeof employee === 'object' && employee?.fullname) return employee.fullname;
    if (typeof employee === 'string') return employee;
    const fromList = employeesData?.find((e) => e.employee_id === formData.employee_id);
    if (fromList) return `${fromList.name} (${fromList.employee_id})`;
    return formData.employee_id || model?.employee_id || 'N/A';
  }, [model?.employee, model?.employee_id, employeesData, formData.employee_id]);

  const workTypeLabel = WORK_TYPE_LABELS[formData.work_type.toLowerCase()] ?? formData.work_type;
  const salesRateDisplay = (() => {
    if (formData.sales_rate === '') return 'N/A';
    const rate = parseFloat(formData.sales_rate);
    return Number.isFinite(rate) ? `€${rate.toFixed(2)}` : 'N/A';
  })();
  const startDateDisplay = formData.start_date
    ? formatDateDdMmYyyy(`${formData.start_date}T00:00:00Z`)
    : 'N/A';
  const endDateDisplay = formData.no_end_date
    ? 'No end date'
    : formData.end_date
      ? formatDateDdMmYyyy(`${formData.end_date}T00:00:00Z`)
      : 'N/A';
  const orderDisplay = formData.order?.trim() ? formData.order : 'N/A';
  const statusDisplay = formData.status ?? 'active';

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && model) {
        setFormData({
          employee_id: model.employee_id || '',
          project_id: model.project_id || '',
          sales_rate: model.sales_rate?.toString() || '',
          work_type: model.work_type || '',
          start_date: model.start_date ? model.start_date.split('T')[0] : '',
          end_date: model.end_date ? model.end_date.split('T')[0] : '',
          no_end_date: model.no_end_date || false,
          status: model.status || 'active',
          order: model.order || '',
        });
      } else {
        setFormData({
          employee_id: '',
          project_id: projectId || '',
          sales_rate: '',
          work_type: '',
          start_date: '',
          end_date: '',
          no_end_date: false,
          order: '',
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, model, projectId]);

  const handleInputChange = (
    field: keyof EmployeeProjectModelFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.employee_id || formData.employee_id.trim() === '') {
      newErrors.employee_id = 'Employee is required';
    }

    if (!formData.sales_rate || formData.sales_rate.trim() === '') {
      newErrors.sales_rate = 'Sales rate is required';
    } else {
      const rate = parseFloat(formData.sales_rate);
      if (Number.isNaN(rate) || rate < 0) {
        newErrors.sales_rate = 'Sales rate must be a valid positive number';
      }
    }

    if (!formData.work_type || formData.work_type.trim() === '') {
      newErrors.work_type = 'Work type is required';
    }

    if (!formData.start_date || formData.start_date.trim() === '') {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.no_end_date && (!formData.end_date || formData.end_date.trim() === '')) {
      newErrors.end_date = 'End date is required when "No end date" is not checked';
    }

    if (formData.start_date && formData.end_date && !formData.no_end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end < start) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData = {
        employee_id: formData.employee_id.trim(),
        project_id: isEdit ? formData.project_id.trim() : projectId || formData.project_id.trim(),
        sales_rate: parseFloat(formData.sales_rate),
        work_type: formData.work_type.trim(),
        start_date: formData.start_date,
        end_date: formData.no_end_date ? new Date('2099-12-31').toISOString() : formData.end_date,
        no_end_date: formData.no_end_date,
        order: formData.order.trim() || '',
        ...(isEdit && formData.status ? { status: formData.status } : { status: 'active' }),
      };

      onSave(apiData);
      onClose();
    } catch (error) {
      logger.error('Error saving employee project model:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        readOnly
          ? 'View Project Member'
          : isEdit
            ? 'Edit Employee Project Model'
            : 'Add Employee Project Model'
      }
      description={
        readOnly
          ? 'Project member details.'
          : isEdit
            ? 'Update the project member details below.'
            : 'Fill in the details for the new project member.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Create'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        {readOnly ? (
          <>
            <TextFormField
              label="Employee"
              required
              value={employeeLabel}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="Sales Rate (€)"
              required
              value={salesRateDisplay}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="Work Type"
              required
              value={workTypeLabel || 'N/A'}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="Order"
              value={orderDisplay}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="Start Date"
              required
              value={startDateDisplay}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="End Date"
              value={endDateDisplay}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="Status"
              value={statusDisplay}
              onChange={() => {}}
              disabled
              copyable
            />
          </>
        ) : (
          <>
            <SelectFormField
              label="Employee"
              required
              value={formData.employee_id}
              onChange={(v) => handleInputChange('employee_id', v)}
              options={employeeOptions}
              placeholder="Select an employee"
              disabled={isLoadingEmployees}
              error={errors.employee_id}
            />
            <TextFormField
              label="Sales Rate (€)"
              type="number"
              required
              value={formData.sales_rate}
              onChange={(v) => handleInputChange('sales_rate', v)}
              error={errors.sales_rate}
            />
            <SelectFormField
              label="Work Type"
              required
              value={formData.work_type}
              onChange={(v) => handleInputChange('work_type', v)}
              options={[
                { value: 'office', label: 'Office' },
                { value: 'site', label: 'Site' },
                { value: 'both', label: 'Both' },
              ]}
              placeholder="Select a work type"
              error={errors.work_type}
            />
            <SelectFormField
              label="Order"
              value={formData.order}
              onChange={(v) => handleInputChange('order', v)}
              options={orderOptions}
              disabled={isLoadingOrders || !currentProjectId}
              error={errors.order}
            />
            <TextFormField
              label="Start Date"
              type="date"
              required
              value={formData.start_date}
              onChange={(v) => handleInputChange('start_date', v)}
              error={errors.start_date}
            />
            <Field>
              <FieldContent>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="no-end-date"
                    checked={formData.no_end_date}
                    onCheckedChange={(checked) => {
                      const noEndDate = checked === true;
                      handleInputChange('no_end_date', noEndDate);
                      if (noEndDate) {
                        handleInputChange('end_date', '');
                      }
                    }}
                  />
                  <Label htmlFor="no-end-date">No end date</Label>
                </div>
              </FieldContent>
            </Field>
            {!formData.no_end_date ? (
              <TextFormField
                label="End Date"
                type="date"
                required
                value={formData.end_date}
                onChange={(v) => handleInputChange('end_date', v)}
                error={errors.end_date}
              />
            ) : null}
            {isEdit ? (
              <SelectFormField
                label="Status"
                value={formData.status || 'active'}
                onChange={(v) => handleInputChange('status', v)}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'archived', label: 'Archived' },
                ]}
              />
            ) : null}
          </>
        )}
      </FieldGroup>
    </FormDetailPane>
  );
};
